export type CurrencyCode =
  | "EUR" | "GBP" | "SEK" | "USD" | "AOA" | "GTQ" | "CHF" | "NOC" | "AED" | "MXN"
  | "FUN" | "BNS" | "ARS" | "CLP" | "VES" | "COP" | "PEN" | "BOB";


export enum CurrencyText {
    EUR = 'eur',
    GBP = 'gbp',
    SEK = 'sek',
    USD = 'usd',
    AOA = 'aoa',
    GTQ = 'gtq',
    CHF = 'chf',
    NOC = 'noc',
    AED = 'aed',
    MXN = 'mxn',
    FUN = 'fun',
    BNS = 'bns',
}

export enum CurrencySymbol {
    USD = "$",
    EUR = "€",
    GBP = "£",
    SEK = "kr",
    CHF = "CHF",
    NOC = "",
    FUN = "FUN",
    BNS = "BNS",
    MXN = "$",
    AED = "DH",
    AOA = "Kz",
    GTQ = "Q",
    ARS = "$",
    CLP = "$",
    VES = "Bs",
    COP = "$",
    PEN = "S/",
    BOB = "$b",
}

export type SymbolPosition = "left" | "right";

export interface CurrencyFormatting {
  divider: number;              // es: 100 (cent), 10, 1...
  locale: string;               // es: "it-IT"
  decimals: number;             // es: 2
  decimalSeparator: string;     // es: "," o "."
  thousandsSeparator: string;   // es: "." o "," o " "
  symbolPosition: SymbolPosition;
}

const DEFAULT_FORMATTING: Record<CurrencyCode, CurrencyFormatting> = {
  USD: { divider: 100, locale: "en-US", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  EUR: { divider: 100, locale: "it-IT", decimals: 2, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "left" },
  GBP: { divider: 100, locale: "en-GB", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  SEK: { divider: 10,  locale: "sv-SE", decimals: 1, decimalSeparator: ",", thousandsSeparator: " ", symbolPosition: "right" },
  CHF: { divider: 100, locale: "de-CH", decimals: 2, decimalSeparator: ".", thousandsSeparator: "'", symbolPosition: "left" },
  NOC: { divider: 100, locale: "en-US", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  FUN: { divider: 100, locale: "en-US", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  BNS: { divider: 100, locale: "en-US", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  MXN: { divider: 100, locale: "es-MX", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  AED: { divider: 100, locale: "ar-AE", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  AOA: { divider: 100, locale: "pt-AO", decimals: 2, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "right" },
  GTQ: { divider: 100, locale: "es-GT", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  ARS: { divider: 100, locale: "es-AR", decimals: 2, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "left" },
  CLP: { divider: 1,   locale: "es-CL", decimals: 0, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "left" },
  VES: { divider: 100, locale: "es-VE", decimals: 2, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "left" },
  COP: { divider: 100, locale: "es-CO", decimals: 0, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "left" },
  PEN: { divider: 100, locale: "es-PE", decimals: 2, decimalSeparator: ".", thousandsSeparator: ",", symbolPosition: "left" },
  BOB: { divider: 1000, locale: "es-BO", decimals: 2, decimalSeparator: ",", thousandsSeparator: ".", symbolPosition: "left" },
};

export class Formatter {
    private static currencyCode: CurrencyCode;
    private static _callbackFormatAmount: ((amount: number) => string) | null = null;

    public static initCurrency(currency: string): void {
        const actualCurrency = currency.toUpperCase() as CurrencyCode;

        this.currencyCode = (actualCurrency in DEFAULT_FORMATTING) ? actualCurrency : "EUR";
    }

    public static formatNumber(value: number, decimals: number = 2): string {
        return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    public static formatCurrency(value: number, useSymbol: boolean = true): string {
        const actualCurrency = this.currencyCode

        //In base al code recupero il formato
        const format = DEFAULT_FORMATTING[actualCurrency];
        const divider = format.divider;
        const decimals = format.decimals;

        if (this._callbackFormatAmount) {
            return this._callbackFormatAmount(value / divider);
        }

        const symbol = CurrencySymbol[actualCurrency] ?? "";
        const formatted = this.formatNumber(value / divider, decimals);

        if(!useSymbol || actualCurrency === "NOC" || symbol === "") return formatted;

        return `${formatted} ${symbol}`;
    }

}